<template>
  <v-container class="account">
    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="12"
        lg="8"
        xl="6"
        offset-lg="2"
        offset-xl="3"
        pt="0"
      >
        <h1>
          {{ $t('payment_information.txt.my_account_details') }}
        </h1>
        <div class="description">
          <div>
            {{ $t('payment_information.txt.description') }}
          </div>
        </div>
        <div
          v-if="loading"
          class="loader"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="12"
        lg="8"
        xl="6"
        offset-lg="2"
        offset-xl="3"
      >
        <v-card>
          <v-form
            v-if="showForm"
            ref="form"
            v-model="valid"
            lazy-validation
            class="form pa-8"
          >
            <img
              :src="$t('payment_information.img.truck.img')"
              :alt="$t('payment_information.img.truck.alt')"
              class="truck d-none d-sm-block"
            >
            <v-row>
              <v-col
                cols="12"
                sm="9"
              >
                <v-row>
                  <v-col
                    cols="12"
                    class="section-title"
                  >
                    <div class="horizontal-spacer" />
                    <h2>{{ $t('payment_information.txt.company_information') }}</h2>
                    <div class="horizontal-spacer" />
                  </v-col>

                  <PaymentInformationInput
                    :key="fieldKey+1"
                    :caption="$t('payment_information.txt.company_id_number')"
                    :is-readonly="true"
                    :name="'accountNumber'"
                    :value="accountNumber"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.company_id_number')"
                    :tooltip-text="$t('payment_information.txt.company_id_number_tt')"
                    :col-size="12"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+2"
                    :caption="$t('payment_information.txt.company_name')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.company_name_ph')"
                    :name="'companyNameInformation'"
                    :value="createAccountFields.companyName"
                    :backup-value="createAccountFieldsBackup.companyName"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.company_name')"
                    :tooltip-text="$t('payment_information.txt.company_name_tt')"
                    :col-size="12"
                    :field-validation="validateRequired(createAccountFields.companyName)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, createAccountFieldsBackup.companyName, createAccountFields, 'companyName', $t('payment_information.txt.company_information'), $t('payment_information.txt.company_name'), $t('payment_information.txt.company_name_change'))"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+3"
                    :caption="$t('payment_information.txt.company_legal_name')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.company_legal_name_ph')"
                    :name="'companyLegalNameInformation'"
                    :value="createAccountFields.companyLegalName"
                    :backup-value="createAccountFieldsBackup.companyLegalName"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.company_legal_name')"
                    :tooltip-text="$t('payment_information.txt.company_legal_name_tt')"
                    :col-size="12"
                    :field-validation="validateRequired(createAccountFields.companyLegalName)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, createAccountFieldsBackup.companyLegalName, createAccountFields, 'companyLegalName', $t('payment_information.txt.company_information'), $t('payment_information.txt.company_legal_name'), $t('payment_information.txt.company_legal_name_change'))"
                  />
                  <v-col cols="12">
                    <span class="bold-text">{{ $t('payment_information.txt.company_address_text') }}</span>
                  </v-col>
                  <div class="container pa-0">
                    <PaymentInformationInput
                      :key="fieldKey+4"
                      :caption="$t('payment_information.txt.address')"
                      :is-readonly="isReadonly"
                      :placeholder="$t('payment_information.txt.address_ph')"
                      :name="'addressInformation'"
                      :value="data.address1"
                      :mandatory="true"
                      :show-info="true"
                      :tooltip-title="$t('payment_information.txt.address')"
                      :tooltip-text="$t('payment_information.txt.address_tt')"
                      :col-size="12"
                      :field-validation="validateRequired(data.address1)"
                      @valueChange="handleChange($event, dataBackup.address1, data, 'address1', $t('payment_information.txt.company_information'), $t('payment_information.txt.address'), null)"
                    />
                    <PaymentInformationInput
                      :key="fieldKey+5"
                      class="mt-n8"
                      :is-readonly="isReadonly"
                      :placeholder="$t('payment_information.txt.address2_ph')"
                      :name="'addressInformation2'"
                      :value="data.address2"
                      :tooltip-title="$t('payment_information.txt.address')"
                      :tooltip-text="$t('payment_information.txt.address_tt')"
                      :col-size="12"
                      @valueChange="handleChange($event, dataBackup.address2, data, 'address2', $t('payment_information.txt.company_information'), $t('payment_information.txt.address2'), null)"
                    />
                  </div>
                  <PaymentInformationInput
                    :key="fieldKey+6"
                    :caption="$t('payment_information.txt.postal_code')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.postal_code_ph')"
                    :name="'postalCodeInformation'"
                    :value="data.zipCode"
                    :mandatory="true"
                    :col-size="6"
                    :field-validation="validateRequired(data.zipCode)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, dataBackup.zipCode, data, 'zipCode', $t('payment_information.txt.company_information'), $t('payment_information.txt.postal_code'), null)"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+7"
                    :caption="$t('payment_information.txt.city')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.city_ph')"
                    :name="'cityInformation'"
                    :value="data.city"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.address2')"
                    :tooltip-text="$t('payment_information.txt.address2_tt')"
                    :col-size="6"
                    :field-validation="validateRequired(data.city)"
                    :error-message="$t('payment_informativalidateRequired(data.city)on.txt.required_field')"
                    @valueChange="handleChange($event, dataBackup.city, data, 'city', $t('payment_information.txt.company_information'), $t('payment_information.txt.city'), null)"
                  />

                  <v-col
                    cols="12"
                    class="py-0"
                  >
                    <v-row>
                      <v-col
                        cols="12"
                        class="pt-4 pb-0"
                      >
                        <span class="caption">{{ $t('payment_information.txt.country') }}</span>
                        <span class="mandatory"> *</span>
                      </v-col>
                      <v-col
                        cols="12"
                        class="field py-0"
                      >
                        <v-autocomplete
                          :key="fieldKey+8"
                          v-model="country"
                          class="select"
                          :readonly="isReadonly"
                          :items="countries"
                          :placeholder="$t('payment_information.txt.country_ph')"
                          name="'countryInformation'"
                          item-text="name"
                          :return-object="true"
                          append-icon="mdi-chevron-down"
                          :menu-props="{ offsetY: true }"
                          filled
                          rounded
                          dense
                          lazy
                          :background-color="country ? 'pale-two' : 'pink-light'"
                          :error-messages="country ? '' : $t('payment_information.txt.required_field')"
                          @change="handleChange($event.name, dataBackup.country, data, 'country', $t('payment_information.txt.company_information'), $t('payment_information.txt.country'), $t('payment_information.txt.country_change'))"
                        />
                        <v-tooltip
                          content-class="info-tooltip"
                          right
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="info-tooltip d-none d-sm-block"
                              v-on="on"
                            >
                              mdi-information-variant
                            </v-icon>
                          </template>
                          <v-card
                            elevation="0"
                            flat
                          >
                            <span class="tooltip-title">{{ $t('payment_information.txt.country_ph') }}</span>
                            <p class="tooltip-text">
                              {{ $t('payment_information.txt.country_tt') }}
                            </p>
                          </v-card>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-col>
                  <PaymentInformationInput
                    :key="fieldKey+9"
                    :caption="$t('payment_information.txt.vat_number')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.vat_number_ph')"
                    :name="'vatNumber'"
                    :value="taxInfo.exemptCertificateId"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.vat_number')"
                    :tooltip-text="$t('payment_information.txt.vat_number_tt')"
                    :col-size="12"
                    :field-validation="validVAT"
                    :checking-vat="checkingVAT"
                    :error-message="$t('payment_information.txt.vat_invalid')"
                    @valueChange="handleChange($event, taxInfoBackup.exemptCertificateId, taxInfo, 'exemptCertificateId', $t('payment_information.txt.company_information'), $t('payment_information.txt.vat_number'), $t('payment_information.txt.vat_change'))"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+10"
                    :caption="$t('payment_information.txt.chamber_number')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.chamber_number_ph')"
                    :name="'chamberCommerceNumber'"
                    :value="legalPerson.chamberCommerceNumber"
                    :backup-value="legalPersonBackup.chamberCommerceNumber"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.chamber_number')"
                    :tooltip-text="$t('payment_information.txt.chamber_number_tt')"
                    :col-size="12"
                    @valueChange="handleChange($event, legalPersonBackup.chamberCommerceNumber, legalPerson, 'chamberCommerceNumber', $t('payment_information.txt.company_information'), $t('payment_information.txt.chamber_number'), $t('payment_information.txt.chamber_number_change'))"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+11"
                    :caption="$t('payment_information.txt.email')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.email_ph')"
                    :name="'workEmail'"
                    :value="data.workEmail"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.email')"
                    :tooltip-text="$t('payment_information.txt.email_tt')"
                    :col-size="12"
                    :field-validation="validateEmail(data.workEmail)"
                    :error-message="$t('payment_information.txt.invalid_email')"
                    @valueChange="handleChange($event, dataBackup.workEmail, data, 'workEmail', $t('payment_information.txt.company_information'), $t('payment_information.txt.email'), null)"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+12"
                    :caption="$t('payment_information.txt.contact_person')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.first_name_ph')"
                    :name="'firstNameInformation'"
                    :value="data.firstName"
                    :mandatory="true"
                    :col-size="6"
                    :field-validation="validateRequired(data.firstName)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, dataBackup.firstName, data, 'firstName', $t('payment_information.txt.company_information'), $t('payment_information.txt.first_name'), null)"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+13"
                    :placeholder="$t('payment_information.txt.last_name_ph')"
                    :name="'lastNameInformation'"
                    :is-readonly="isReadonly"
                    :value="data.lastName"
                    :mandatory="false"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.contact_person')"
                    :tooltip-text="$t('payment_information.txt.contact_person_tt')"
                    :col-size="6"
                    class="mt-n8 mt-sm-5"
                    :field-validation="validateRequired(data.lastName)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, dataBackup.lastName, data, 'lastName', $t('payment_information.txt.company_information'), $t('payment_information.txt.last_name'), null)"
                  />
                </v-row>

                <v-row>
                  <v-col>
                    <v-checkbox
                      v-model="differentAddress"
                      class="invoice-checkbox"
                      :label="$t('payment_information.txt.invoice_address')"
                      value=""
                      :ripple="false"
                      color="peacock"
                    />
                  </v-col>
                </v-row>
                <v-row v-if="differentAddress">
                  <v-col
                    cols="12"
                    class="section-title"
                  >
                    <div class="horizontal-spacer" />
                    <h2>{{ $t('payment_information.txt.invoice_information') }}</h2>
                    <div class="horizontal-spacer" />
                  </v-col>
                  <div class="container pa-0">
                    <PaymentInformationInput
                      :key="fieldKey+14"
                      :caption="$t('payment_information.txt.address')"
                      :is-readonly="isReadonly"
                      :placeholder="$t('payment_information.txt.address_ph')"
                      :name="'streetNameInvoice'"
                      :value="invoice.address1"
                      :mandatory="true"
                      :show-info="true"
                      :tooltip-title="$t('payment_information.txt.address')"
                      :tooltip-text="$t('payment_information.txt.invoice_address_tt')"
                      :col-size="12"
                      :field-validation="validateRequired(invoice.address1)"
                      @valueChange="handleChange($event, invoiceBackup.address1, invoice, 'address1', $t('payment_information.txt.invoice_information'), $t('payment_information.txt.address'), null)"
                    />
                    <PaymentInformationInput
                      :key="fieldKey+15"
                      class="mt-n8"
                      :is-readonly="isReadonly"
                      :placeholder="$t('payment_information.txt.address2_ph')"
                      :name="'addressInformation2'"
                      :value="invoice.address2"
                      :tooltip-title="$t('payment_information.txt.address')"
                      :tooltip-text="$t('payment_information.txt.invoice_address_tt')"
                      :col-size="12"
                      @valueChange="handleChange($event, invoiceBackup.address2, invoice, 'address2', $t('payment_information.txt.invoice_information'), $t('payment_information.txt.address2'), null)"
                    />
                  </div>
                  <PaymentInformationInput
                    :key="fieldKey+16"
                    :caption="$t('payment_information.txt.postal_code')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.postal_code_ph')"
                    :name="'postalCodeInvoice'"
                    :value="invoice.zipCode"
                    :mandatory="true"
                    :col-size="6"
                    :field-validation="validateRequired(invoice.zipCode)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, invoiceBackup.zipCode, invoice, 'zipCode', $t('payment_information.txt.invoice_information'), $t('payment_information.txt.postal_code'), null)"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+17"
                    :caption="$t('payment_information.txt.city')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.city_ph')"
                    :name="'cityInvoice'"
                    :value="invoice.city"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.address2')"
                    :tooltip-text="$t('payment_information.txt.invoice_address2_tt')"
                    :col-size="6"
                    :field-validation="validateRequired(invoice.city)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, invoiceBackup.city, invoice, 'city', $t('payment_information.txt.invoice_information'), $t('payment_information.txt.city'), null)"
                  />
                  <v-col
                    cols="12"
                    class="pt-4 pb-0"
                  >
                    <v-row>
                      <v-col
                        cols="12"
                        class="py-0"
                      >
                        <span class="caption">{{ $t('payment_information.txt.country') }}</span>
                        <span class="mandatory"> *</span>
                      </v-col>
                      <v-col
                        cols="12"
                        class="field py-0"
                      >
                        <v-autocomplete
                          :key="fieldKey+18"
                          :value="invoice.country"
                          class="select"
                          :items="countries"
                          :readonly="isReadonly"
                          :placeholder="$t('payment_information.txt.country_ph')"
                          name="'countryInvoice'"
                          item-text="name"
                          :return-object="true"
                          append-icon="mdi-chevron-down"
                          :menu-props="{ offsetY: true }"
                          filled
                          rounded
                          dense
                          lazy
                          :background-color="invoice.country ? 'pale-two' : 'pink-light'"
                          :error-messages="invoice.country ? '' : $t('payment_information.txt.required_field')"
                          @change="handleChange($event.name, invoiceBackup.country, invoice, 'country', $t('payment_information.txt.invoice_information'), $t('payment_information.txt.country'), $t('payment_information.txt.country_change'))"
                        />
                        <v-tooltip
                          content-class="info-tooltip"
                          right
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="info-tooltip d-none d-sm-block"
                              v-on="on"
                            >
                              mdi-information-variant
                            </v-icon>
                          </template>
                          <v-card
                            elevation="0"
                            flat
                          >
                            <span class="tooltip-title">{{ $t('payment_information.txt.country_ph') }}</span>
                            <p class="tooltip-text">
                              {{ $t('payment_information.txt.invoice_country_tt') }}
                            </p>
                          </v-card>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row>
                  <PaymentInformationInput
                    :key="fieldKey+19"
                    :caption="$t('payment_information.txt.invoice_email')"
                    :is-readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.email_ph')"
                    :name="'invoiceEmailAddress'"
                    :value="invoice.workEmail"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.invoice_email')"
                    :tooltip-text="$t('payment_information.txt.invoice_email_tt')"
                    :col-size="12"
                    :field-validation="validateEmail(invoice.workEmail)"
                    :error-message="$t('payment_information.txt.invalid_email')"
                    @valueChange="handleChange($event, invoiceBackup.workEmail, invoice, 'workEmail', $t('payment_information.txt.invoice_information'), $t('payment_information.txt.invoice_email'), null)"
                  />
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    class="section-title mt-8"
                  >
                    <div class="horizontal-spacer" />
                    <h2>{{ $t('payment_information.txt.legal_person') }}</h2>
                    <div class="horizontal-spacer" />
                  </v-col>

                  <PaymentInformationInput
                    :key="fieldKey+20"
                    :caption="$t('payment_information.txt.first_name')"
                    :readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.first_name_ph')"
                    :name="'firstNameLegalPerson'"
                    :value="legalPerson.firstName"
                    :mandatory="true"
                    :col-size="6"
                    :field-validation="validateRequired(legalPerson.firstName)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, legalPersonBackup.firstName, legalPerson, 'firstName', $t('payment_information.txt.legal_person'), $t('payment_information.txt.first_name'), null)"
                  />
                  <PaymentInformationInput
                    :key="fieldKey+21"
                    :caption="$t('payment_information.txt.last_name')"
                    :readonly="isReadonly"
                    :placeholder="$t('payment_information.txt.last_name_ph')"
                    :name="'lastNameLegalPerson'"
                    :value="legalPerson.lastName"
                    :mandatory="true"
                    :show-info="true"
                    :tooltip-title="$t('payment_information.txt.legal_person')"
                    :tooltip-text="$t('payment_information.txt.legal_person_tt')"
                    :col-size="6"
                    :field-validation="validateRequired(legalPerson.lastName)"
                    :error-message="$t('payment_information.txt.required_field')"
                    @valueChange="handleChange($event, legalPersonBackup.lastName, legalPerson, 'lastName', $t('payment_information.txt.legal_person'), $t('payment_information.txt.last_name'), null)"
                  />
                  <v-col
                    cols="12"
                    class="pt-4 pb-0"
                  >
                    <v-row>
                      <v-col
                        cols="12"
                        class="py-0"
                        rowspan="3"
                      >
                        <span class="caption">{{ $t('payment_information.txt.date_birth') }}</span>
                        <span class="mandatory"> *</span>
                      </v-col>
                      <v-col
                        cols="12"
                        sm="4"
                        class="field py-0"
                      >
                        <v-select
                          :key="fieldKey+22"
                          :value="legalPerson.dayBirth"
                          class="select"
                          :items="days"
                          :readonly="isReadonly"
                          :placeholder="$t('payment_information.txt.day_ph')"
                          name="'dayBirth'"
                          append-icon="mdi-chevron-down"
                          :menu-props="{ offsetY: true }"
                          filled
                          rounded
                          dense
                          lazy
                          :return-object="true"
                          :background-color="legalPerson.dayBirth ? 'pale-two' : 'pink-light'"
                          :error-messages="legalPerson.dayBirth ? '' : $t('payment_information.txt.required_field')"
                          @change="handleChange($event.value, legalPersonBackup.dayBirth, legalPerson, 'dayBirth', $t('payment_information.txt.legal_person'), $t('payment_information.txt.day_birth'), null)"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="4"
                        class="field py-0"
                      >
                        <v-select
                          :key="fieldKey+23"
                          :value="legalPerson.monthBirth"
                          class="select mt-n5 mt-sm-0"
                          :items="months"
                          :readonly="isReadonly"
                          item-text="name"
                          item-value="number"
                          name="'monthBirth'"
                          :placeholder="$t('payment_information.txt.month_ph')"
                          append-icon="mdi-chevron-down"
                          :menu-props="{ offsetY: true }"
                          filled
                          rounded
                          dense
                          lazy
                          :return-object="true"
                          :background-color="legalPerson.monthBirth ? 'pale-two' : 'pink-light'"
                          :error-messages="legalPerson.monthBirth ? '' : $t('payment_information.txt.required_field')"
                          @change="handleChange($event.number, legalPersonBackup.monthBirth, legalPerson, 'monthBirth', $t('payment_information.txt.legal_person'), $t('payment_information.txt.month_birth'), null)"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="4"
                        class="field py-0"
                      >
                        <v-select
                          :key="fieldKey+24"
                          :value="legalPerson.yearBirth"
                          class="select mt-n5 mt-sm-0"
                          :items="years"
                          :readonly="isReadonly"
                          :placeholder="$t('payment_information.txt.year_ph')"
                          name="'yearBirth'"
                          append-icon="mdi-chevron-down"
                          :menu-props="{ offsetY: true }"
                          filled
                          rounded
                          dense
                          lazy
                          :return-object="true"
                          :background-color="legalPerson.yearBirth ? 'pale-two' : 'pink-light'"
                          :error-messages="legalPerson.yearBirth ? '' : $t('payment_information.txt.required_field')"
                          @change="handleChange($event.value, legalPersonBackup.yearBirth, legalPerson, 'yearBirth', $t('payment_information.txt.legal_person'), $t('payment_information.txt.year_birth'), null)"
                        />
                        <v-tooltip
                          content-class="info-tooltip"
                          right
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="info-tooltip d-none d-sm-block"
                              v-on="on"
                            >
                              mdi-information-variant
                            </v-icon>
                          </template>
                          <v-card
                            elevation="0"
                            flat
                          >
                            <span class="tooltip-title">{{ $t('payment_information.txt.date_birth') }}</span>
                            <p class="tooltip-text">
                              {{ $t('payment_information.txt.date_birth_tt') }}
                            </p>
                          </v-card>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col
                    cols="12"
                    class="pt-4 pb-0"
                  >
                    <v-row>
                      <v-col
                        cols="12"
                        class="py-0"
                      >
                        <span class="caption">{{ $t('payment_information.txt.country') }}</span>
                        <span class="mandatory"> *</span>
                      </v-col>
                      <v-col
                        cols="12"
                        class="field py-0"
                      >
                        <v-autocomplete
                          :key="fieldKey+25"
                          :value="legalPerson.country"
                          class="select"
                          :items="countries"
                          :readonly="isReadonly"
                          :placeholder="$t('payment_information.txt.country_ph')"
                          name="'countryLegalPerson'"
                          item-text="name"
                          :return-object="true"
                          append-icon="mdi-chevron-down"
                          :menu-props="{ offsetY: true }"
                          filled
                          rounded
                          dense
                          lazy
                          :background-color="legalPerson.country ? 'pale-two' : 'pink-light'"
                          :error-messages="legalPerson.country ? '' : $t('payment_information.txt.required_field')"
                          @change="handleChange($event.name, legalPersonBackup.country, legalPerson, 'country', $t('payment_information.txt.legal_person'), $t('payment_information.txt.country'), $t('payment_information.txt.country_change'))"
                        />
                        <v-tooltip
                          content-class="info-tooltip"
                          right
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="info-tooltip d-none d-sm-block"
                              v-on="on"
                            >
                              mdi-information-variant
                            </v-icon>
                          </template>
                          <v-card
                            elevation="0"
                            flat
                          >
                            <span class="tooltip-title">{{ $t('payment_information.txt.country_ph') }}</span>
                            <p class="tooltip-text">
                              {{ $t('payment_information.txt.legal_country_tt') }}
                            </p>
                          </v-card>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                    class="proceed"
                  >
                    <v-btn
                      class="mt-6"
                      :disabled="isDisabled"
                      @click="checkChanges"
                    >
                      {{ $t('payment_information.txt.proceed') }}
                    </v-btn>
                  </v-col>
                </v-row>
                <v-dialog
                  v-model="dialogChanges"
                  width="600"
                  height="600"
                  max-width="700"
                >
                  <v-card class="overflow-hidden">
                    <v-card-title class="headline">
                      {{ $t('payment_information.txt.accept_changes') }}
                    </v-card-title>
                    <v-card-text
                      v-for="sorted in sortedChanges"
                      :key="sorted.index"
                    >
                      <v-card-subtitle class="pl-0 pb-0">
                        {{ sorted[0].type }}
                      </v-card-subtitle>
                      <div
                        v-for="change in sorted"
                        :key="change.title"
                        class="flex"
                      >
                        <label class="py-0 dialog-changes-label">
                          {{ change.title }}:
                        </label>
                        <label class="py-1 dialog-changes-value">
                          {{ change.new }}
                        </label>
                      </div>
                    </v-card-text>
                    <v-progress-linear
                      v-if="submitted"
                      indeterminate
                      color="primary"
                    />
                    <v-card-actions v-else>
                      <v-spacer />
                      <v-btn
                        color="red darken-1"
                        text
                        @click="rollbackChanges"
                      >
                        {{ $t('payment_information.txt.no') }}
                      </v-btn>

                      <v-btn
                        color="green darken-1"
                        text
                        @click="submit"
                      >
                        {{ $t('payment_information.txt.yes') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-col>
            </v-row>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar
      v-model="notification"
      top
      :color="notificationType"
    >
      {{ notificationText }}
      <v-btn
        color="white"
        text
        @click="notification = false"
      >
        {{ $t('payment_information.txt.close') }}
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import PaymentInformationInput from '@/components/PaymentInformationInput.vue'
import moment from 'moment'
import { mapActions } from 'vuex'

export default {
  name: 'PaymentInformation',
  components: {
    PaymentInformationInput
  },
  data() {
    return {
      accountDetails: {},
      createAccountFields: {
        companyLegalName: '',
        companyName: ''
      },
      data: {
        lastName: '',
        workEmail: '',
        firstName: '',
        address1: '',
        address2: '',
        zipCode: '',
        city: '',
        country: ''
      },
      taxInfo: {
        exemptCertificateId: ''
      },
      invoice: {
        firstName: '',
        address1: '',
        address2: '',
        zipCode: '',
        city: '',
        country: '',
        workEmail: ''
      },
      legalPerson: {
        firstName: '',
        lastName: '',
        dayBirth: '',
        monthBirth: '',
        yearBirth: '',
        country: '',
        chamberCommerceNumber: ''
      },
      accountNumber: '',
      invoiceEmail: '',
      country: {},
      differentAddress: false,
      item: 0,
      monthsWith30Days: ['4', '6', '9', '11'],
      countries: [],
      dialog: false,
      valid: true,
      submitted: false,
      rules: {
        //required: value => !!value,
        numbers: value => {
          const pattern = /^[0-9]*$/
          return pattern.test(value) || 'Invalid phone number'
        },
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        }
      },
      validVAT: false,
      isReadonly: false,
      createAccountFieldsBackup: {},
      dataBackup: {},
      taxInfoBackup: {},
      invoiceBackup: {},
      legalPersonBackup: {},
      dialogChanges: false,
      dataChanged: [],
      newChange: false,
      sortedChanges: [],
      fieldKey: 0,
      cddCheck: '',
      notification: false,
      notificationText: '',
      notificationType: 'success',
      loading: false,
      showForm: false,
      checkingVAT: false,
      timeoutID: null
    }
  },
  computed: {
    years() {
      const year = []
      const currentYear = new Date().getFullYear()
      for (let i = 1960; i <= currentYear; ++i) {
        year.push({ text: i, value: i.toString() })
      }
      return year
    },
    months() {
      const month = []
      const tt = moment.localeData(this.$i18n.locale).months()
      tt.forEach((element, i) => {
        i++
        month.push({ name: element, number: i.toString() })
      })
      return month
    },
    days() {
      const day = []
      let lastday
      if (this.monthsWith30Days.includes(this.month)) {
        lastday = 30
      } else if (this.leapYear(this.year)) {
        lastday = 29
      }
      if (this.legalPerson.monthBirth === '02' && !this.leapYear(this.year)) {
        lastday = 28
      } else {
        lastday = 31
      }
      for (let i = 1; i <= lastday; ++i) {
        day.push({ text: i, value: i.toString() })
      }
      return day
    },
    isDisabled: function() {
      const checks = {
        dataChangedLength: !this.dataChanged.length,
        companyName: !this.validateRequired(this.createAccountFields.companyName),
        companyLegalName: !this.validateRequired(this.createAccountFields.companyLegalName),
        address1: !this.validateRequired(this.data.address1),
        zipCode: !this.validateRequired(this.data.zipCode),
        city: !this.validateRequired(this.data.city),
        workEmail: !this.validateEmail(this.data.workEmail),
        firstName: !this.validateRequired(this.data.firstName),
        lastName: !this.validateRequired(this.data.lastName),
        invoiceWorkEmail: !this.validateEmail(this.invoice.workEmail),
        legalPersonFirstName: !this.validateRequired(this.legalPerson.firstName),
        legalPersonLastName: !this.validateRequired(this.legalPerson.lastName),
        // Only validate yearBirth if it was changed (if unchanged and invalid, don't block save)
        legalPersonYearBirth: this.hasYearBirthChanged()
          ? !this.validateBirth(this.legalPerson.yearBirth)
          : false,
        invoiceAddress: this.differentAddress &&
          (!this.validateRequired(this.invoice.address1) ||
            !this.validateRequired(this.invoice.zipCode) ||
            !this.validateRequired(this.invoice.city))
      }

      return Object.values(checks).some(v => v === true)
    },

    formatVatNumber() {
      if (this.taxInfo.exemptCertificateId === '') return ''

      const vatNumber = this.taxInfo.exemptCertificateId
      const includeCountryCode = this.countries.find(item =>
        vatNumber.startsWith(item.alpha2Code)
      )
      if (includeCountryCode) {
        return vatNumber
      } else {
        return `${this.country.alpha2Code}${vatNumber}`
      }
    }
  },
  async created() {
    await this.getCountries()
    await this.getAccountInformation()
    await this.setCountry()
    await this.setEditUserData()
    await this.validateVAT()
    await this.checkAddress()
  },
  methods: {
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('account', ['getAccountDetails']),
    leapYear: function(year) {
      return (year % 4 == 0 && year % 100 != 0) || year % 400 == 0
    },
    checkAddress() {
      if (
        this.data.address1 === this.invoice.address1 &&
        this.data.address2 === this.invoice.address2 &&
        this.data.zipCode === this.invoice.zipCode &&
        this.data.city === this.invoice.city &&
        this.data.country === this.invoice.country
      )
        this.differentAddress = false
      else this.differentAddress = true
    },
    handleChange(
      newValue,
      defaultValue,
      object,
      field,
      dialogtype,
      dialogtitle,
      dialogtext
    ) {
      this.$set(object, field, newValue)
      // Normalize values for comparison (handle undefined, null, empty strings)
      const normalizedNew = newValue != null ? String(newValue).trim() : ''
      const normalizedDefault = defaultValue != null ? String(defaultValue).trim() : ''

      // Check if values are different
      const hasChanged = normalizedNew !== normalizedDefault

      if (hasChanged) {
        const recorded = this.dataChanged.find(
          data => data.type === dialogtype && data.title === dialogtitle
        )
        if (recorded) {
          this.$set(recorded, 'new', newValue)
        } else {
          this.dataChanged.push({
            type: dialogtype,
            title: dialogtitle,
            new: newValue,
            default: defaultValue
          })
        }
        if (dialogtext && this.cddCheck) {
          this.setSnackbar({
            showing: true,
            type: 'navy',
            text: dialogtext
          })
        }
      } else if (this.dataChanged.length) {
        const indexToRemove = this.dataChanged.findIndex(
          item => item.type === dialogtype && item.title === dialogtitle
        )
        if (indexToRemove !== -1) {
          this.dataChanged.splice(indexToRemove, 1)
        }
      }
      if (field === 'country') {
        if (this.formatVatNumber.startsWith(this.country.alpha2Code)) {
          this.validateVAT()
        } else {
          this.validVAT = this.taxInfo.exemptCertificateId === ''
        }
      }
      if (field === 'exemptCertificateId') {
        this.validateVAT()
      }
    },
    getAccountInformation() {
      this.loading = true
      return apiCalls
        .get(api.AccountDetails, '', '')
        .then(res => {
          this.cddCheck = res.data.CddCheck
          this.accountNumber = res.data.serviceSubscriber
          this.createAccountFields.companyLegalName =
            res.data.CompanyInformation.companyLegalName
          this.createAccountFields.companyName =
            res.data.CompanyInformation.companyName
          this.data = res.data.CompanyInformation.soldToContact
          this.taxInfo = res.data.CompanyInformation.taxInfo
          this.invoice = res.data.InvoiceInformation
          this.legalPerson = res.data.legalPerson
          if (res.data.InvoiceInformation.workEmail === '') {
            this.invoice.workEmail = this.data.workEmail
          }
          this.createAccountFieldsBackup.companyLegalName = JSON.parse(
            JSON.stringify(this.createAccountFields.companyLegalName)
          )
          this.createAccountFieldsBackup.companyName = JSON.parse(
            JSON.stringify(this.createAccountFields.companyName)
          )
          this.dataBackup = JSON.parse(JSON.stringify(this.data))
          this.taxInfoBackup = JSON.parse(JSON.stringify(this.taxInfo))
          this.invoiceBackup = JSON.parse(JSON.stringify(this.invoice))
          this.legalPersonBackup = JSON.parse(JSON.stringify(this.legalPerson))
          this.country.name = res.data.CompanyInformation.soldToContact.country
          // Update store with loaded data
          this.$store.commit('account/setAccountInformation', res.data)
        })
        .finally(() => {
          this.loading = false
          this.showForm = true
        })
    },
    getCountries() {
      return apiCalls.get(api.Countries, '', '').then(res => {
        this.countries = res.data
      })
    },
    setCountry() {
      this.countries.forEach(country => {
        if (country.name === this.data.country) {
          this.country = country
        }
      })
    },
    validateVAT() {
      if (this.taxInfo.exemptCertificateId === '') {
        this.validVAT = true
        return
      }

      if (this.timeoutID) {
        clearTimeout(this.timeoutID)
        this.timeoutID = null
      }
      this.timeoutID = setTimeout(async () => {
        try {
          this.checkingVAT = true
          const res = await apiCalls.post(
            api.CheckVAT,
            '',
            '',
            "{country:'" +
              this.country.alpha2Code +
              "', vat_ID:'" +
              this.formatVatNumber +
              "'}"
          )
          if (res.status === 200) {
            this.validVAT = true
          } else {
            this.validVAT = false
          }
        } catch (ex) {
          this.validVAT = false
        } finally {
          this.checkingVAT = false
        }
      }, 500)
    },
    resetValidation() {
      this.$refs.form.resetValidation()
    },
    resetForm() {
      this.$refs.form.reset()
    },
    checkChanges() {
      if (this.dataChanged.length > 0) {
        this.sortedChanges = this.dataChanged.reduce(function(r, a, i) {
          if (!i || r[r.length - 1][0].type !== a.type) {
            return r.concat([[a]])
          }
          r[r.length - 1].push(a)
          return r
        }, [])
        this.dialogChanges = true
      } else this.submit()
    },
    async rollbackChanges() {
      this.dialogChanges = false
      this.dataChanged = []
      await this.getAccountInformation()
      this.fieldKey = Math.random()
    },
    async submit() {
      try {
        this.submitted = true
        if (!this.differentAddress) {
          this.invoice.address1 = this.data.address1
          this.invoice.address2 = this.data.address2
          this.invoice.zipCode = this.data.zipCode
          this.invoice.city = this.data.city
          this.invoice.country = this.data.country
          this.invoice.firstName = this.data.firstName
        }

        const response = await apiCalls.put(api.AccountUpdate, '', '', {
          accountNumber: this.accountNumber,
          cddCheck: this.cddCheck,
          companyInformation: {
            soldToContact: this.data,
            taxInfo: {
              ...this.taxInfo,
              exemptCertificateId: this.formatVatNumber
            },
            companyName: this.createAccountFields.companyName,
            companyLegalName: this.createAccountFields.companyLegalName
          },
          invoiceInformation: this.invoice,
          legalPerson: this.legalPerson
        })
        if (response.status == 200) {
          // Forçar refresh para garantir que os dados mais recentes sejam carregados
          await this.getAccountDetails(true)
          if (!this.cddCheck) {
            this.$router.push({
              name: 'payment_method'
            })
          } else {
            this.$router.push({
              name: 'home'
            })
          }

          this.setSnackbar({
            showing: true,
            type: 'success',
            text: this.$t('payment_information.txt.notification_success')
          })
        } else {
          this.setSnackbar({
            showing: true,
            type: 'error',
            text: this.$t('payment_information.txt.notification_error')
          })
        }
      } finally {
        this.submitted = false
        this.dialogChanges = false
      }
    },
    setEditUserData() {
      let checkEditUserData = true
      if (this.$route.params.editUserData) {
        checkEditUserData = this.$route.params.editUserData
      }
      if (checkEditUserData === true) this.isReadonly = false
      //put true
      else this.isReadonly = true
    },
    validateRequired(value) {
      return !!value
    },
    validateBirth(yearBirth) {
      if (!yearBirth || yearBirth === '') return false
      const year = parseInt(yearBirth)
      return !isNaN(year) && year >= 1960
    },
    validateEmail(value) {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(value)
    },
    hasYearBirthChanged() {
      return this.dataChanged.some(
        change => change.title === this.$t('payment_information.txt.year_birth')
      )
    }
  }
}
</script>

<style lang="scss">
.account {
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .description {
    font-size: 0.875rem;
    color: var(--v-slate-base);
  }
  .form {
    font-size: 0.875rem;
    position: relative;
    overflow: initial;
    @media screen and (max-width: 960px) {
      overflow: hidden;
      border-radius: 10px 10px 0 0;
    }
    .field {
      position: relative;
      .select {
        font-size: 0.875rem;
        .v-input__slot {
          border: 1px solid var(--v-pale-three-base) !important;
        }
        .v-icon {
          margin-top: 0 !important;
        }
      }
      .info-tooltip {
        color: var(--v-bluegrey-base) !important;
        border-radius: 8px;
        border: solid 1px;
        height: 16px;
        width: 16px;
        font-size: 0.875rem !important;
        position: absolute;
        top: 12px;
        right: -12px;
      }
    }
    .truck {
      top: 32px;
      right: -116px;
      display: flex;
      width: 232px;
      position: absolute;
      z-index: 2;
    }
    .section-title {
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      padding: 0 16px 30px;
      h2 {
        margin: 0 10px;
        color: var(--v-bluegrey-base) !important;
        text-transform: uppercase;
        font-size: 0.75rem;
        font-weight: normal;
      }
      .horizontal-spacer {
        border-top: 1px solid var(--v-pale-three-base);
        -webkit-box-flex: 1;
        -ms-flex-positive: 1;
        flex-grow: 1;
      }
    }
    .invoice-checkbox {
      margin-bottom: 20px;
      .v-label {
        font-size: 0.875rem;
        color: var(--v-dark-base);
      }
    }
    .payment-buttons {
      .v-btn {
        font-size: 0.875rem;
        font-weight: bold;
        text-align: center;
        border: solid 1px var(--v-cloudy-base) !important;
        border-radius: 4px !important;
        width: 100%;
      }
    }
    .proceed {
      .v-btn {
        height: 48px !important;
        border-radius: 5px;
        background-image: linear-gradient(
          to bottom,
          #266cab 0%,
          #01529c
        ) !important;
        font-size: 0.875rem;
        font-weight: bold;
        text-align: center;
        color: white !important;
        width: 100%;
        &--disabled {
          background-image: none !important;
        }
      }
    }
  }
  .vat-checked {
    margin-top: -25px;
    padding: 0 24px;
    font-size: 0.75rem;
    font-weight: bold;
    color: var(--v-success-darken1);
  }
}
.dialog-changes-label {
  font-size: 0.75rem;
  font-weight: bold;
  color: var(--v-mid-base) !important;
  text-transform: uppercase;
}
.dialog-changes-value {
  font-size: 0.75rem;
}
.payment-iframe {
  width: 100%;
  height: 440px;
}
.bold-text {
  font-size: 0.875rem;
  font-weight: bold;
  color: var(--v-dark-base);
}
</style>